import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookitupScope, ProfileS, ProfileShare, ProfileSharePatch } from '../ProfileS';

// eslint-disable-next-line import/prefer-default-export
export const useBookitupTeam = (profileId?: number) => {
  const [loading, setLoading] = useState(false);
  const [sharesLoaded, setSharesLoaded] = useState(false);
  const [shares, setShares] = useState<ProfileShare[]>([]);
  const [selectedApplicationUserId, setSelectedApplicationUserId] = useState<number>();
  const pendingSharesRequestRef = useRef<Promise<ProfileShare[]> | null>(null);
  const profileIdRef = useRef(profileId);

  useEffect(() => {
    profileIdRef.current = profileId;
    pendingSharesRequestRef.current = null;
    setLoading(false);
    setSharesLoaded(false);
    setShares([]);
    setSelectedApplicationUserId(undefined);
  }, [profileId]);

  const fetchShares = useCallback(() => {
    if (!profileIdRef.current) {
      setShares([]);
      setSharesLoaded(false);
      return Promise.resolve([]);
    }

    if (pendingSharesRequestRef.current) {
      return pendingSharesRequestRef.current;
    }

    const requestProfileId = profileIdRef.current;
    setLoading(true);

    const request = ProfileS.getShareEntitiesOfProfile()
      .then((fetchedShares) => {
        if (profileIdRef.current === requestProfileId) {
          setShares(fetchedShares);
          setSharesLoaded(true);
        }

        return fetchedShares;
      })
      .catch((error) => {
        if (profileIdRef.current === requestProfileId) {
          setShares([]);
          setSharesLoaded(false);
        }

        throw error;
      })
      .finally(() => {
        if (pendingSharesRequestRef.current === request) {
          pendingSharesRequestRef.current = null;
        }

        if (profileIdRef.current === requestProfileId) {
          setLoading(false);
        }
      });

    pendingSharesRequestRef.current = request;
    return request;
  }, []);

  const refresh = useCallback(async () => {
    await fetchShares();
  }, [fetchShares]);

  const ensureSharesLoaded = useCallback(() => {
    if (sharesLoaded) {
      return Promise.resolve(shares);
    }

    return fetchShares();
  }, [fetchShares, shares, sharesLoaded]);

  const selectedShare = useMemo(
    () => shares.find((share) => share.applicationUserId === selectedApplicationUserId),
    [selectedApplicationUserId, shares],
  );

  useEffect(() => {
    if (shares.length === 0) {
      if (selectedApplicationUserId) {
        setSelectedApplicationUserId(undefined);
      }

      return;
    }

    if (!shares.some((share) => share.applicationUserId === selectedApplicationUserId)) {
      setSelectedApplicationUserId(shares[0].applicationUserId);
    }
  }, [selectedApplicationUserId, shares]);

  const patchShare = useCallback(async (applicationUserId: number, scopes: BookitupScope[]) => {
    const updatedShare = await ProfileS.patchShare(applicationUserId, { scopes });

    if (updatedShare) {
      setShares((prevShares) =>
        prevShares.map((share) => (share.applicationUserId === applicationUserId ? updatedShare : share)),
      );
    }

    return updatedShare;
  }, []);

  const patchShareSettings = useCallback(async (applicationUserId: number, patch: ProfileSharePatch) => {
    const updatedShare = await ProfileS.patchShare(applicationUserId, patch);

    if (updatedShare) {
      setShares((prevShares) =>
        prevShares.map((share) => (share.applicationUserId === applicationUserId ? updatedShare : share)),
      );
    }

    return updatedShare;
  }, []);

  return useMemo(
    () => ({
      loading,
      sharesLoaded,
      shares,
      selectedShare,
      selectedApplicationUserId,
      selectShare: setSelectedApplicationUserId,
      patchShare,
      patchShareSettings,
      refresh,
      ensureSharesLoaded,
    }),
    [
      loading,
      sharesLoaded,
      shares,
      selectedShare,
      selectedApplicationUserId,
      patchShare,
      patchShareSettings,
      refresh,
      ensureSharesLoaded,
    ],
  );
};
